import { AddComment } from '@app/core/model/add-comment';
import { ChangeLogEvent, FileAttachment } from '@app/core/db/change-log';
import { UpdateComment } from '@app/core/model/update-comment';
import { Attachment, CommentControllerService, CommentDto } from '@app/generated';
import { DeleteComment } from '@app/core/model/delete-comment';
import { EventEmitter, Injectable, Injector, Output } from '@angular/core';
import { AbstractWorkOrderDataService } from '@app/work-order/abstract-work-order-data.service';
import { BehaviorSubject } from 'rxjs';
import { UserService } from '@dwo/services';
import { HttpClient } from '@angular/common/http';
import { ChangeLogService } from '@app/core/services/change-log.service';
import { v4 as uuid } from 'uuid';
import { env } from '../../environments/env';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderCommentsService extends AbstractWorkOrderDataService<CommentDto[]> {
  @Output() commentsUpdated = new EventEmitter<any>();

  private isLoaded = false;
  public comments$ = new BehaviorSubject<CommentDto[]>([]);

  constructor(
    private commentControllerService: CommentControllerService,

    private userService: UserService,
    private httpClient: HttpClient,
    protected changeLogService: ChangeLogService,
    injector: Injector,
  ) {
    super(injector, 'COMMENTS');

    this.getData().subscribe((comments) => this.comments$.next(comments));
  }

  load(): void {
    this.commentControllerService.getComments(this.workOrderId).subscribe({
      next: (comments) => {
        this.setData(comments);
        this.isLoaded = true;
      },
      error: (error) => {
        console.warn(`[COMMENTS] Failed to load comments for ${this.workOrderId}.`, error);
        void this.handleLoadFailure([], error);
        this.isLoaded = true;
      },
    });
  }

  commentsLoaded() {
    return this.isLoaded;
  }

  // Downloading used to pull offline blobs.
  // Now attachments live in PostgreSQL, so there is nothing to download upfront.
  async download() {
    await super.download();
  }

  async addReply(replyData: {
    parentId: string;
    referenceId: string;
    referenceName: string;
    referenceType: CommentDto.ReferenceTypeEnum;
    text: string;
  }): Promise<void> {
    const replyId = uuid();

    const payload: AddComment = {
      id: replyId,
      parentId: replyData.parentId,
      text: replyData.text,
      referenceId: replyData.referenceId,
      referenceName: replyData.referenceName,
      referenceType: replyData.referenceType,
      workOrderId: this.workOrderId,
    };

    await this.changeLogService.publish('WO_ADD_COMMENT', payload, [this.workOrderId], []);

    // Keep reply UX optimistic and non-blocking; sync service reconciles with backend.
    this.applyLocalChange('WO_ADD_COMMENT', payload);
    this.commentsUpdated.emit();
  }

  // ---------------------------------------------
  // ADD COMMENT
  // ---------------------------------------------
  async addComment(commentData: {
    referenceId: string;
    referenceName: string;
    referenceType: CommentDto.ReferenceTypeEnum;
    text: string;
    attachmentName: string | null;
    attachmentTag: string | null;
    attachmentContentType?: string;
    file?: File | Blob | null;
  }) {
    const commentId = uuid();

    const payload: AddComment = {
      id: commentId,
      text: commentData.text,
      referenceId: commentData.referenceId,
      referenceName: commentData.referenceName,
      referenceType: commentData.referenceType,
      workOrderId: this.workOrderId,
    };

    const files: FileAttachment[] = [];

    if (commentData.file) {
      files.push({
        id: commentId, // CRUCIAL — matches DB endpoint
        content: commentData.file,
      });
      payload.attachmentId = commentId;
    }

    await this.changeLogService.publish('WO_ADD_COMMENT', payload, [this.workOrderId], files);

    // Optimistic local update so offline-created comments are visible immediately.
    this.applyLocalChange('WO_ADD_COMMENT', payload);

    // The change is queued and will be synced by ChangeSyncService when connectivity is restored.
    // No polling needed here — the optimistic update already shows the comment immediately.
    this.commentsUpdated.emit();
  }

  // ---------------------------------------------
  // UPDATE COMMENT
  // ---------------------------------------------
  async updateComment(commentData: {
    commentId: string;
    referenceId: string;
    referenceName: string;
    referenceType: CommentDto.ReferenceTypeEnum;
    text: string;
    attachmentName: string | null;
    attachmentTag: string | null;
    file?: File | Blob | null;
  }) {
    // -----------------------------------
    // 1. Upload file directly (if present)
    // -----------------------------------
    if (commentData.file) {
      await this.uploadCommentAttachment(this.workOrderId, commentData.commentId, commentData.file);
    }

    // -----------------------------------
    // 2. Prepare UPDATE payload
    // -----------------------------------
    const payload: UpdateComment = {
      id: commentData.commentId,
      text: commentData.text,
      referenceId: commentData.referenceId,
      referenceName: commentData.referenceName,
      referenceType: commentData.referenceType,
      tag: commentData.attachmentTag,
      workOrderId: this.workOrderId,

      // If a new file was chosen, set attachmentId and type.
      // If not, send null to preserve existing attachment.
      attachmentId: commentData.file ? commentData.commentId : null,
      attachmentType: commentData.file?.type ?? null,
    };

    // -----------------------------------
    // 3. Publish CHANGELOG WITHOUT attachments
    // -----------------------------------
    await this.changeLogService.publish(
      'WO_UPDATE_COMMENT',
      payload,
      [this.workOrderId],
      undefined, // << IMPORTANT: DO NOT SEND FILES HERE
    );

    // Optimistic local update for immediate UI feedback while queued.
    this.applyLocalChange('WO_UPDATE_COMMENT', payload);

    // -----------------------------------
    // 4. Emit update event for UI refresh
    // -----------------------------------
    this.commentsUpdated.emit();
  }

  // ---------------------------------------------
  // DELETE COMMENT
  // ---------------------------------------------
  async deleteComment(
    commentId: string,
    referenceId: string,
    referenceName: string,
    referenceType: CommentDto.ReferenceTypeEnum,
  ) {
    const payload: DeleteComment = {
      id: commentId,
      referenceId,
      referenceName,
      referenceType,
      workOrderId: this.workOrderId,
    };

    await this.changeLogService.publish('WO_DELETE_COMMENT', payload, [this.workOrderId], []);

    // Optimistic local update for immediate UI feedback while queued.
    this.applyLocalChange('WO_DELETE_COMMENT', payload);

    this.commentsUpdated.emit();
  }

  uploadCommentAttachment(workOrderId: string, commentId: string, file: File | Blob): Promise<void> {
    const fd = new FormData();

    const filename = (file as File).name ?? `upload_${new Date().toISOString().replace(/[:.]/g, '-')}.bin`;

    // Part name MUST be "file" to match @RequestParam("file")
    fd.append('file', file, filename);

    return this.httpClient
      .post<void>(
        `${env.get().api.url}/work-orders/${encodeURIComponent(workOrderId)}/comments/${encodeURIComponent(
          commentId,
        )}/attachment`,
        fd,
      )
      .toPromise();
  }

  // ---------------------------------------------
  // APPLY CHANGELOG EVENTS → UPDATE LOCAL STATE
  // ---------------------------------------------
  protected apply(comments: CommentDto[], event: ChangeLogEvent) {
    const list = Array.isArray(comments) ? comments : [];
    const payload: any = event.content;
    const nowUser = this.userService.get().username;

    const comment: CommentDto = {
      id: payload.id,
      parentId: payload.parentId,
      text: payload.text,
      referenceId: payload.referenceId,
      referenceName: payload.referenceName,
      referenceType: payload.referenceType,
      createdBy: nowUser,
      createdTime: new Date(event.created).toISOString(),
      attachment: undefined,
    };

    if (payload.attachmentId) {
      comment.attachment = {
        url: `${env.get().api.url}/work-orders/${payload.workOrderId}/comments/${
          payload.id
        }/attachment?ts=${Date.now()}`,
        tag: payload.tag,
        type: payload.attachmentType,
      } as Attachment;
    }

    switch (event.type) {
      case 'WO_ADD_COMMENT':
        if (this.commentExists(list, comment.id)) {
          break;
        }

        if (comment.parentId) {
          const parent = list.find((c) => c.id === comment.parentId);
          if (parent) {
            const replies = Array.isArray(parent.replies) ? [...parent.replies] : [];
            if (!replies.some((r) => r.id === comment.id)) {
              replies.push(comment);
              parent.replies = replies;
            }
            break;
          }

          // Do not place replies at root level when parent linkage exists.
          break;
        }

        if (!list.some((c) => c.id === comment.id)) {
          list.unshift(comment);
        }
        break;

      case 'WO_UPDATE_COMMENT':
        this.applyCommentUpdate(list, comment);
        break;

      case 'WO_DELETE_COMMENT': {
        const idx = list.findIndex((c) => c.id === comment.id);
        if (idx >= 0) {
          list.splice(idx, 1);
        } else {
          list.forEach((root) => {
            const replies = root.replies as CommentDto[] | undefined;
            if (!Array.isArray(replies)) return;
            const ri = replies.findIndex((r) => r.id === comment.id);
            if (ri >= 0) replies.splice(ri, 1);
          });
        }
        break;
      }
    }

    this.setData([...list]);
  }

  private applyLocalChange(type: string, payload: any) {
    const event: ChangeLogEvent = {
      key: `local-${payload?.id || uuid()}`,
      type,
      status: 'PENDING',
      content: payload,
      refs: [this.workOrderId],
      created: Date.now(),
      updated: Date.now(),
    };

    this.apply(this.get() ?? [], event);
  }

  private commentExists(comments: CommentDto[], id: string): boolean {
    return comments.some((c) => c.id === id || (Array.isArray(c.replies) && c.replies.some((r) => r.id === id)));
  }

  private applyCommentUpdate(list: CommentDto[], comment: CommentDto): void {
    const mergeWithCurrent = (current: CommentDto): CommentDto => {
      const merged: CommentDto = {
        ...current,
        ...comment,
        parentId: comment.parentId ?? current.parentId,
      };

      if (!comment.attachment && current.attachment) {
        merged.attachment = current.attachment;
      }

      return merged;
    };

    const topLevelIndex = list.findIndex((item) => item.id === comment.id);
    if (topLevelIndex >= 0) {
      list[topLevelIndex] = mergeWithCurrent(list[topLevelIndex]);
      return;
    }

    list.forEach((root) => {
      const replies = root.replies as CommentDto[] | undefined;
      if (!Array.isArray(replies)) {
        return;
      }

      const replyIndex = replies.findIndex((reply) => reply.id === comment.id);
      if (replyIndex >= 0) {
        const nextReplies = [...replies];
        nextReplies[replyIndex] = mergeWithCurrent(nextReplies[replyIndex]);
        root.replies = nextReplies;
      }
    });
  }
}
